/** GET /freshness and the landing-page line it feeds (BUG-22, docs/decisions/0010).
 *
 *  `freshnessText` is pure (tested in __tests__/lib/freshness.test.ts);
 *  `fetchFreshness` is the network edge and returns null on any failure —
 *  the line is a courtesy and must never block the chat. */

export interface SeasonExamples {
  cup: string | null;
  move_change: string | null;
  new_move: { pokemon: string; move: string } | null;
}

export interface Freshness {
  data_as_of: string | null;
  season: { name: string; ends_at: string | null; is_upcoming: boolean; examples?: SeasonExamples } | null;
}

const BASE = import.meta.env.VITE_API_BASE_URL || "";

// Season names arrive as "GO Battle League: Twilight Trails"; the line already says "GBL season".
const SEASON_PREFIX = /^\s*(GO Battle League|GBL)\s*[:\-–—]\s*/i;

/** "Data as of Oct 25 · GBL season: Forever Forward", or null when there is nothing to say. */
export function freshnessText(f: Freshness | null): string | null {
  if (!f) return null;
  const parts: string[] = [];
  if (f.data_as_of) {
    const date = new Date(f.data_as_of);
    if (!Number.isNaN(date.getTime())) {
      parts.push(`Data as of ${date.toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: "UTC" })}`);
    }
  }
  const season = seasonShortName(f);
  if (season) {
    parts.push(f.season?.is_upcoming ? `Next GBL season: ${season}` : `GBL season: ${season}`);
  }
  return parts.length ? parts.join(" · ") : null;
}

/** The season's name without the "GO Battle League:" prefix, or null. */
export function seasonShortName(f: Freshness | null): string | null {
  const name = f?.season?.name?.replace(SEASON_PREFIX, "").trim();
  return name ? name : null;
}

/** The "GBL Season" prompts for the "What can I ask?" sheet, built from the current season.
 *
 *  Each named example (season, cup, move, Pokémon) comes from /freshness, so
 *  the sheet never suggests last season's cup or move; any example the
 *  season lacks falls back to a phrasing without a name. */
export function seasonPrompts(f: Freshness | null): string[] {
  const season = seasonShortName(f);
  const ex = f?.season?.examples;
  // Three prompts, one per answer family (plan phase 0b, 2026-11-04): the season summary,
  // the cup schedule and one cup's rules; the season's name only when the API has it.
  return [
    season ? `What's new in ${season}?` : "What's new in the GBL season?",
    "What cups are coming up?",
    ex?.cup ? `What's allowed in ${ex.cup}?` : "What are this season's cup rules?",
  ];
}

let pending: Promise<Freshness | null> | null = null;

/** /freshness, fetched once per page load and shared by every caller. */
export function getFreshness(): Promise<Freshness | null> {
  pending ??= fetchFreshness();
  return pending;
}

export async function fetchFreshness(): Promise<Freshness | null> {
  try {
    const resp = await fetch(`${BASE}/freshness`);
    if (!resp.ok) return null;
    return (await resp.json()) as Freshness;
  } catch {
    return null;
  }
}
